import { FormEvent, useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { getErrorMessage } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  CalendarioEventi as Calendario,
  CorpoEvento,
  EventoRitiri,
  TipoDataEvento,
  aggiornaEvento,
  calendarioEventi,
  creaEvento,
  eliminaEvento,
  impostaAttivoEvento,
} from "../../api/ritiri";
import { PulsanteRitiri } from "../../components/ritiri/TestataRitiri";
import { numero } from "./presentazione";
import { CALCOLO_CALENDARIO_EVENTI, INTRO_CALENDARIO_EVENTI } from "./testiLegenda";

/**
 * Sezione "Calendario eventi" della scheda Regole (§ 4 bis): elenco, modifica,
 * eliminazione e interruttore attivo / non attivo con `ritiri:update`; chi ha
 * solo `ritiri:read` vede l'elenco. Le regole (date ammesse, limiti del
 * moltiplicatore, doppioni) le controlla il backend: qui si mostra il suo
 * messaggio.
 */

const MESI_ESTESI = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

/** Nell'ordine della colonna `giorno_settimana`: 1 = lunedì … 7 = domenica. */
const GIORNI_SETTIMANA = ["lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];
const DOMENICA = 7;
/** `ordinale` dell'ultimo di quei giorni nel mese; il 5° non c'è in tutti i mesi. */
const ULTIMO = -1;
const ORDINALI = [1, 2, 3, 4, ULTIMO];

const campo = "mt-1 h-10 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink";
const etichetta = "block text-xs font-bold text-ritiri-th-testo";

/** "2026-03-29" → "29/03". */
function ggmm(giorno: string): string {
  return `${giorno.slice(8, 10)}/${giorno.slice(5, 7)}`;
}

function due(n: number | null): string {
  return String(n ?? 0).padStart(2, "0");
}

function giorniDallaPasqua(n: number): string {
  if (n === 0) return "la domenica di Pasqua";
  const giorni = Math.abs(n) === 1 ? "1 giorno" : `${Math.abs(n)} giorni`;
  return n < 0 ? `${giorni} prima di Pasqua` : `${giorni} dopo Pasqua`;
}

/** "4° giovedì", "ultima domenica": la domenica è femminile. */
function quale(ordinale: number, giorno_settimana: number): string {
  const femminile = giorno_settimana === DOMENICA;
  const n = ordinale === ULTIMO ? (femminile ? "ultima" : "ultimo") : `${ordinale}${femminile ? "ª" : "°"}`;
  return `${n} ${GIORNI_SETTIMANA[giorno_settimana - 1] ?? "?"}`;
}

function giorniDa(n: number): string {
  if (n === 0) return "lo stesso giorno";
  const giorni = Math.abs(n) === 1 ? "1 giorno" : `${Math.abs(n)} giorni`;
  return n < 0 ? `${giorni} prima` : `${giorni} dopo`;
}

/** Le date dell'evento come le ha scritte chi l'ha inserito. */
function regolaDate(e: EventoRitiri): string {
  if (e.tipo_data === "settimana_mese") {
    const rif = `${quale(e.ordinale ?? 1, e.giorno_settimana ?? 1)} di ${MESI_ESTESI[(e.mese_riferimento ?? 1) - 1]}`;
    const si = e.scostamento_inizio ?? 0;
    const sf = e.scostamento_fine ?? 0;
    if (si === 0 && sf === 0) return `il ${rif}`;
    return si === sf ? `${rif}, ${giorniDa(si)}` : `${rif}, da ${giorniDa(si)} a ${giorniDa(sf)}`;
  }
  if (e.tipo_data === "pasqua") {
    const da = giorniDallaPasqua(e.scostamento_inizio ?? 0);
    return e.scostamento_inizio === e.scostamento_fine ? da : `da ${da} a ${giorniDallaPasqua(e.scostamento_fine ?? 0)}`;
  }
  const inizio = `${due(e.giorno_inizio)}/${due(e.mese_inizio)}`;
  const fine = `${due(e.giorno_fine)}/${due(e.mese_fine)}`;
  return inizio === fine ? `il ${inizio}` : `dal ${inizio} al ${fine}`;
}

function descriviEffetto(x: { categoria_attivita: string; regione: string | null; moltiplicatore: number; ritardo_giorni: number }) {
  return {
    dove: `${x.categoria_attivita} · ${x.regione ?? "tutta Italia"}`,
    quanto: `×${numero(x.moltiplicatore, 2)}`,
    ritardo: x.ritardo_giorni > 0 ? `${x.ritardo_giorni} ${x.ritardo_giorni === 1 ? "giorno" : "giorni"} dopo` : null,
  };
}

// ----------------------------------------------------------------------------
// Modulo di inserimento e modifica
// ----------------------------------------------------------------------------

/** I campi come stringhe, come li scrive l'utente; diventano numeri all'invio. */
interface RigaEffetto {
  chiave: number;
  categoria_attivita: string;
  regione: string;
  moltiplicatore: string;
  ritardo_giorni: string;
}

interface Bozza {
  nome: string;
  tipo_data: TipoDataEvento;
  giorno_inizio: string;
  mese_inizio: string;
  giorno_fine: string;
  mese_fine: string;
  scostamento_inizio: string;
  scostamento_fine: string;
  mese_riferimento: string;
  ordinale: string;
  giorno_settimana: string;
  note: string;
  effetti: RigaEffetto[];
}

let prossimaChiave = 1;

function rigaVuota(categoria: string): RigaEffetto {
  return { chiave: prossimaChiave++, categoria_attivita: categoria, regione: "", moltiplicatore: "", ritardo_giorni: "0" };
}

function bozzaDa(e: EventoRitiri | null, categorie: string[]): Bozza {
  if (!e) {
    return {
      nome: "",
      tipo_data: "fissa",
      giorno_inizio: "",
      mese_inizio: "12",
      giorno_fine: "",
      mese_fine: "12",
      scostamento_inizio: "-7",
      scostamento_fine: "-1",
      mese_riferimento: "11",
      ordinale: "4",
      giorno_settimana: "4",
      note: "",
      effetti: [rigaVuota(categorie[0] ?? "")],
    };
  }
  const s = (n: number | null) => (n === null ? "" : String(n));
  return {
    nome: e.nome,
    tipo_data: e.tipo_data,
    giorno_inizio: s(e.giorno_inizio),
    mese_inizio: s(e.mese_inizio ?? 12),
    giorno_fine: s(e.giorno_fine),
    mese_fine: s(e.mese_fine ?? 12),
    scostamento_inizio: s(e.scostamento_inizio ?? -7),
    scostamento_fine: s(e.scostamento_fine ?? -1),
    mese_riferimento: s(e.mese_riferimento ?? 11),
    ordinale: s(e.ordinale ?? 4),
    giorno_settimana: s(e.giorno_settimana ?? 4),
    note: e.note ?? "",
    effetti: e.effetti.map((x) => ({
      chiave: prossimaChiave++,
      categoria_attivita: x.categoria_attivita,
      regione: x.regione ?? "",
      moltiplicatore: numero(x.moltiplicatore, 2),
      ritardo_giorni: String(x.ritardo_giorni),
    })),
  };
}

/**
 * Bozza → corpo per il backend. Un numero illeggibile diventa un messaggio qui,
 * invece di arrivare al server come `null`; il resto lo valida il backend.
 */
function corpoDa(b: Bozza): CorpoEvento | string {
  const intero = (v: string) => (v.trim() === "" ? NaN : Number(v));
  const effetti = [];
  for (const [i, x] of b.effetti.entries()) {
    const m = Number(x.moltiplicatore.trim().replace(",", "."));
    if (x.moltiplicatore.trim() === "" || !Number.isFinite(m)) return `Effetto ${i + 1}: moltiplicatore non valido (es. 1,25).`;
    const r = intero(x.ritardo_giorni);
    if (!Number.isInteger(r)) return `Effetto ${i + 1}: ritardo non valido (un numero intero da 0 a 7).`;
    effetti.push({ categoria_attivita: x.categoria_attivita, regione: x.regione || null, moltiplicatore: m, ritardo_giorni: r });
  }
  const fissa = b.tipo_data === "fissa";
  const settimana = b.tipo_data === "settimana_mese";
  const date = fissa
    ? [intero(b.giorno_inizio), intero(b.mese_inizio), intero(b.giorno_fine), intero(b.mese_fine)]
    : [intero(b.scostamento_inizio), intero(b.scostamento_fine)];
  if (date.some((n) => !Number.isInteger(n))) {
    if (fissa) return "Indicare giorno e mese di inizio e di fine.";
    return settimana ? "Indicare i giorni da quel giorno all'inizio e alla fine." : "Indicare i giorni dalla Pasqua di inizio e di fine.";
  }
  return {
    nome: b.nome,
    tipo_data: b.tipo_data,
    giorno_inizio: fissa ? date[0] : null,
    mese_inizio: fissa ? date[1] : null,
    giorno_fine: fissa ? date[2] : null,
    mese_fine: fissa ? date[3] : null,
    scostamento_inizio: fissa ? null : date[0],
    scostamento_fine: fissa ? null : date[1],
    // Dai menu: sempre un numero valido.
    mese_riferimento: settimana ? Number(b.mese_riferimento) : null,
    ordinale: settimana ? Number(b.ordinale) : null,
    giorno_settimana: settimana ? Number(b.giorno_settimana) : null,
    note: b.note.trim() || null,
    effetti,
  };
}

function ModuloEvento({
  evento,
  categorie,
  regioni,
  onSalvato,
  onAnnulla,
}: {
  evento: EventoRitiri | null;
  categorie: string[];
  regioni: string[];
  onSalvato: (e: EventoRitiri) => void;
  onAnnulla: () => void;
}) {
  const [b, setB] = useState<Bozza>(() => bozzaDa(evento, categorie));
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const id = evento?.id ?? "nuovo";

  const imposta = <K extends keyof Bozza>(k: K, v: Bozza[K]) => setB((x) => ({ ...x, [k]: v }));
  const impostaEffetto = (chiave: number, k: keyof Omit<RigaEffetto, "chiave">, v: string) =>
    setB((x) => ({ ...x, effetti: x.effetti.map((r) => (r.chiave === chiave ? { ...r, [k]: v } : r)) }));

  async function salva(ev: FormEvent) {
    ev.preventDefault();
    setErrore(null);
    const corpo = corpoDa(b);
    if (typeof corpo === "string") {
      setErrore(corpo);
      return;
    }
    setInCorso(true);
    try {
      onSalvato(evento ? await aggiornaEvento(evento.id, corpo) : await creaEvento(corpo));
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile salvare l'evento."));
    } finally {
      setInCorso(false);
    }
  }

  const selettoreMese = (k: "mese_inizio" | "mese_fine", nome: string) => (
    <select aria-label={nome} className={campo} value={b[k]} onChange={(e) => imposta(k, e.target.value)}>
      {MESI_ESTESI.map((m, i) => (
        <option key={m} value={String(i + 1)}>
          {m}
        </option>
      ))}
    </select>
  );

  return (
    <form onSubmit={salva} className="space-y-4 rounded-xl border border-ritiri-bordo-forte bg-ritiri-th-fondo p-4" data-eventi="modulo">
      <h3 className="text-sm font-bold text-ink">{evento ? `Modifica «${evento.nome}»` : "Nuovo evento"}</h3>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_20rem]">
        <label className={etichetta}>
          Nome
          <input type="text" required maxLength={100} className={campo} value={b.nome} onChange={(e) => imposta("nome", e.target.value)} />
        </label>
        <label className={etichetta}>
          Date
          <select className={campo} value={b.tipo_data} onChange={(e) => imposta("tipo_data", e.target.value as TipoDataEvento)}>
            <option value="fissa">Fisse ogni anno</option>
            <option value="pasqua">Legate alla Pasqua</option>
            <option value="settimana_mese">Legate a un giorno della settimana</option>
          </select>
        </label>
      </div>

      {b.tipo_data === "fissa" ? (
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="sr-only">Date fisse</legend>
          <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
            <label className={etichetta}>
              Dal giorno
              <input
                type="number"
                min={1}
                max={31}
                required
                className={campo}
                value={b.giorno_inizio}
                onChange={(e) => imposta("giorno_inizio", e.target.value)}
              />
            </label>
            <div className={etichetta}>
              <span aria-hidden="true">mese</span>
              {selettoreMese("mese_inizio", "Mese di inizio")}
            </div>
          </div>
          <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
            <label className={etichetta}>
              Al giorno
              <input type="number" min={1} max={31} required className={campo} value={b.giorno_fine} onChange={(e) => imposta("giorno_fine", e.target.value)} />
            </label>
            <div className={etichetta}>
              <span aria-hidden="true">mese</span>
              {selettoreMese("mese_fine", "Mese di fine")}
            </div>
          </div>
          <p className="text-xs text-ritiri-th-testo sm:col-span-2">
            Estremi inclusi. Se la fine viene prima dell'inizio l'evento passa all'anno dopo (es. dal 27/12 al 02/01).
          </p>
        </fieldset>
      ) : b.tipo_data === "settimana_mese" ? (
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="sr-only">Date legate a un giorno della settimana</legend>
          <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2 sm:col-span-2 sm:grid-cols-[6rem_minmax(0,1fr)_minmax(0,1fr)]">
            <label className={etichetta}>
              Quale
              <select className={campo} value={b.ordinale} onChange={(e) => imposta("ordinale", e.target.value)}>
                {ORDINALI.map((o) => (
                  <option key={o} value={String(o)}>
                    {o === ULTIMO ? "ultimo" : `${o}°`}
                  </option>
                ))}
              </select>
            </label>
            <label className={etichetta}>
              Giorno
              <select className={campo} value={b.giorno_settimana} onChange={(e) => imposta("giorno_settimana", e.target.value)}>
                {GIORNI_SETTIMANA.map((g, i) => (
                  <option key={g} value={String(i + 1)}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
            <label className={clsx(etichetta, "col-span-2 sm:col-span-1")}>
              Di
              <select className={campo} value={b.mese_riferimento} onChange={(e) => imposta("mese_riferimento", e.target.value)}>
                {MESI_ESTESI.map((m, i) => (
                  <option key={m} value={String(i + 1)}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className={etichetta}>
            Inizio: giorni da quel giorno
            <input
              type="number"
              min={-60}
              max={60}
              required
              className={campo}
              value={b.scostamento_inizio}
              onChange={(e) => imposta("scostamento_inizio", e.target.value)}
            />
          </label>
          <label className={etichetta}>
            Fine: giorni da quel giorno
            <input
              type="number"
              min={-60}
              max={60}
              required
              className={campo}
              value={b.scostamento_fine}
              onChange={(e) => imposta("scostamento_fine", e.target.value)}
            />
          </label>
          <p className="text-xs text-ritiri-th-testo sm:col-span-2">
            0 = quel giorno, negativi = prima, positivi = dopo; estremi inclusi. Black Friday: 4° giovedì di novembre, da 1 a 1; Cyber
            Monday: stesso giovedì, da 4 a 4.
          </p>
        </fieldset>
      ) : (
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="sr-only">Date legate alla Pasqua</legend>
          <label className={etichetta}>
            Inizio: giorni dalla Pasqua
            <input
              type="number"
              min={-60}
              max={60}
              required
              className={campo}
              value={b.scostamento_inizio}
              onChange={(e) => imposta("scostamento_inizio", e.target.value)}
            />
          </label>
          <label className={etichetta}>
            Fine: giorni dalla Pasqua
            <input
              type="number"
              min={-60}
              max={60}
              required
              className={campo}
              value={b.scostamento_fine}
              onChange={(e) => imposta("scostamento_fine", e.target.value)}
            />
          </label>
          <p className="text-xs text-ritiri-th-testo sm:col-span-2">
            0 = la domenica di Pasqua, negativi = prima, positivi = dopo; estremi inclusi. La settimana prima è da −7 a −1.
          </p>
        </fieldset>
      )}

      <fieldset>
        <legend className={etichetta}>Effetti</legend>
        <ul className="mt-1 space-y-2">
          {b.effetti.map((x, i) => (
            <li
              key={x.chiave}
              className="grid gap-2 rounded-[11px] border border-ritiri-bordo bg-white p-2 sm:grid-cols-2 sm:items-end xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_7rem_7rem_auto]"
            >
              <label className={etichetta}>
                Categoria
                <select className={campo} value={x.categoria_attivita} onChange={(e) => impostaEffetto(x.chiave, "categoria_attivita", e.target.value)}>
                  {categorie.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label className={etichetta}>
                Regione
                <select className={campo} value={x.regione} onChange={(e) => impostaEffetto(x.chiave, "regione", e.target.value)}>
                  <option value="">Tutta Italia</option>
                  {regioni.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <label className={etichetta}>
                Moltiplicatore
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  placeholder="1,25"
                  className={clsx(campo, "font-plex")}
                  value={x.moltiplicatore}
                  onChange={(e) => impostaEffetto(x.chiave, "moltiplicatore", e.target.value)}
                />
              </label>
              <label className={etichetta}>
                Ritardo (giorni)
                <input
                  type="number"
                  min={0}
                  max={7}
                  required
                  className={campo}
                  value={x.ritardo_giorni}
                  onChange={(e) => impostaEffetto(x.chiave, "ritardo_giorni", e.target.value)}
                />
              </label>
              <button
                type="button"
                onClick={() => setB((s) => ({ ...s, effetti: s.effetti.filter((r) => r.chiave !== x.chiave) }))}
                disabled={b.effetti.length === 1}
                aria-label={`Togli l'effetto ${i + 1}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-[11px] border border-ritiri-bordo-forte text-ink hover:bg-ritiri-hover disabled:opacity-40"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setB((s) => ({ ...s, effetti: [...s.effetti, rigaVuota(categorie[0] ?? "")] }))}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Aggiungi effetto
        </button>
        <p className="mt-1 text-xs text-ritiri-th-testo">
          Moltiplicatore da 0,10 a 5,00 (1,00 = nessun effetto). Il ritardo sposta l'effetto di quei giorni dopo le date dell'evento.
        </p>
      </fieldset>

      <label className={etichetta}>
        Note
        <textarea
          maxLength={1000}
          rows={2}
          className="mt-1 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 py-2 text-sm text-ink"
          value={b.note}
          onChange={(e) => imposta("note", e.target.value)}
        />
      </label>

      {errore && (
        <p className="text-sm text-danger" role="alert" id={`errore-evento-${id}`}>
          {errore}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <PulsanteRitiri type="submit" inCorso={inCorso}>
          {evento ? "Salva modifiche" : "Aggiungi evento"}
        </PulsanteRitiri>
        <PulsanteRitiri type="button" variante="secondario" onClick={onAnnulla} disabled={inCorso}>
          Annulla
        </PulsanteRitiri>
      </div>
    </form>
  );
}

// ----------------------------------------------------------------------------
// Elenco
// ----------------------------------------------------------------------------

/**
 * `onCambiato` scatta dopo ogni scrittura riuscita: gli eventi entrano nel
 * motore, quindi la pagina ricarica la situazione (esiti, indici, dettaglio).
 */
export function CalendarioEventi({ onCambiato }: { onCambiato: () => void }) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const puoModificare = hasPermission("ritiri:update");
  const [dati, setDati] = useState<Calendario | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  /** L'evento nel modulo: `nuovo`, l'id di quello in modifica, o null se il modulo è chiuso. */
  const [inModifica, setInModifica] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState<string | null>(null);

  const carica = useCallback(() => {
    let valida = true;
    calendarioEventi()
      .then((d) => valida && setDati(d))
      .catch((err) => valida && setErrore(getErrorMessage(err, "Impossibile caricare il calendario eventi.")));
    return () => {
      valida = false;
    };
  }, []);
  useEffect(carica, [carica]);

  async function cambiaAttivo(e: EventoRitiri) {
    setInCorso(e.id);
    try {
      await impostaAttivoEvento(e.id, !e.attivo);
      showToast(e.attivo ? `«${e.nome}» non è più attivo.` : `«${e.nome}» è attivo.`, "success");
      carica();
      onCambiato();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile cambiare lo stato dell'evento."), "error");
    } finally {
      setInCorso(null);
    }
  }

  async function elimina(e: EventoRitiri) {
    const effetti = e.effetti.length === 1 ? "il suo effetto" : `i suoi ${e.effetti.length} effetti`;
    if (!window.confirm(`Eliminare l'evento «${e.nome}» con ${effetti}? Non si può annullare.`)) return;
    setInCorso(e.id);
    try {
      await eliminaEvento(e.id);
      showToast("Evento eliminato.", "success");
      if (inModifica === e.id) setInModifica(null);
      carica();
      onCambiato();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare l'evento."), "error");
    } finally {
      setInCorso(null);
    }
  }

  function salvato(e: EventoRitiri) {
    showToast(inModifica === "nuovo" ? `Evento «${e.nome}» aggiunto, non attivo.` : `Evento «${e.nome}» salvato.`, "success");
    setInModifica(null);
    carica();
    onCambiato();
  }

  if (errore) return <p className="text-sm text-ink">{errore}</p>;
  if (!dati) return <p className="text-sm text-ritiri-th-testo">Caricamento…</p>;

  const modulo = (e: EventoRitiri | null) => (
    <ModuloEvento evento={e} categorie={dati.categorie} regioni={dati.regioni} onSalvato={salvato} onAnnulla={() => setInModifica(null)} />
  );

  return (
    <div>
      <p className="mb-2 text-sm text-ink">{INTRO_CALENDARIO_EVENTI}</p>
      <p className="mb-3 text-sm text-ink">{CALCOLO_CALENDARIO_EVENTI}</p>

      {dati.eventi.length === 0 ? (
        <p className="rounded-xl border border-ritiri-bordo px-3 py-6 text-center text-sm text-ritiri-th-testo">Nessun evento inserito.</p>
      ) : (
        <ul className="divide-y divide-ritiri-bordo rounded-xl border border-ritiri-bordo" data-eventi="elenco">
          {dati.eventi.map((e) =>
            inModifica === e.id ? (
              <li key={e.id} className="p-3">
                {modulo(e)}
              </li>
            ) : (
              <li key={e.id} className={clsx("grid gap-3 p-3 text-sm sm:grid-cols-[minmax(0,1fr)_auto]", !e.attivo && "bg-ritiri-th-fondo/60")} data-evento={e.id}>
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-ink">{e.nome}</span>
                    <span
                      className={clsx(
                        "rounded-full px-2 py-0.5 text-xs font-bold",
                        e.attivo ? "bg-ritiro-fisso text-white" : "bg-ritiro-ok text-ritiri-th-testo"
                      )}
                    >
                      {e.attivo ? "Attivo" : "Non attivo"}
                    </span>
                  </div>
                  <p className="text-ink">
                    {regolaDate(e)}
                    <span className="text-ritiri-th-testo">
                      {" — "}
                      {e.date.map((d) => `${d.anno}: ${d.inizio === d.fine ? ggmm(d.inizio) : `${ggmm(d.inizio)}–${ggmm(d.fine)}`}`).join(" · ")}
                    </span>
                  </p>
                  <ul className="flex flex-wrap gap-1.5" aria-label={`Effetti di ${e.nome}`}>
                    {e.effetti.map((x) => {
                      const d = descriviEffetto(x);
                      return (
                        <li key={x.id} className="rounded-[9px] border border-ritiri-bordo bg-white px-2 py-1 text-xs text-ink">
                          {d.dove} <strong className="font-plex">{d.quanto}</strong>
                          {d.ritardo && <span className="text-ritiri-th-testo"> · {d.ritardo}</span>}
                        </li>
                      );
                    })}
                  </ul>
                  {e.note && <p className="break-words text-xs text-ritiri-th-testo">{e.note}</p>}
                </div>
                {puoModificare && (
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={e.attivo}
                      aria-label={`Evento ${e.nome} attivo`}
                      onClick={() => cambiaAttivo(e)}
                      disabled={inCorso === e.id}
                      className={clsx(
                        "relative inline-flex h-9 w-16 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 motion-reduce:transition-none",
                        e.attivo ? "border-ritiro-fisso bg-ritiro-fisso" : "border-ritiri-bordo-forte bg-ritiri-traccia"
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={clsx(
                          "inline-block h-7 w-7 rounded-full bg-white shadow transition-transform motion-reduce:transition-none",
                          e.attivo ? "translate-x-8" : "translate-x-1"
                        )}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => setInModifica(e.id)}
                      disabled={inModifica !== null}
                      aria-label={`Modifica l'evento ${e.nome}`}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-[11px] border border-ritiri-bordo-forte text-ink hover:bg-ritiri-hover disabled:opacity-40"
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => elimina(e)}
                      disabled={inCorso === e.id}
                      aria-label={`Elimina l'evento ${e.nome}`}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-[11px] border border-ritiri-bordo-forte text-ink hover:bg-ritiri-hover disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </li>
            )
          )}
        </ul>
      )}

      {puoModificare &&
        (inModifica === "nuovo" ? (
          <div className="mt-4">{modulo(null)}</div>
        ) : (
          <PulsanteRitiri type="button" variante="secondario" className="mt-4" onClick={() => setInModifica("nuovo")} disabled={inModifica !== null}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nuovo evento
          </PulsanteRitiri>
        ))}
    </div>
  );
}
